import { hashValue } from './hashing'

export type AutoColorTheme = 'light' | 'dark'

export interface AutoColorBadgeColors {
  background: string
  text: string
}

/**
 * Generates deterministic badge colors based on input text and theme.
 *
 * Uses djb2 hashing to create a consistent hue from the input string,
 * then adjusts lightness based on theme for WCAG accessibility:
 * - Light theme: 35% lightness with white text (contrast ratio ~4.5:1)
 * - Dark theme: 80% lightness with dark text (contrast ratio ~4.5:1)
 *
 * @param text - The string to hash for color generation
 * @param theme - 'light' or 'dark' theme mode
 * @returns AutoColorBadgeColors object with background and text colors
 * @throws Error if text is null/undefined or empty
 */
export function getAutoColorBadgeColors(
  text: string | null | undefined,
  theme: AutoColorTheme,
): AutoColorBadgeColors {
  if (text === null || text === undefined) {
    throw new Error('getAutoColorBadgeColors: text cannot be null or undefined')
  }

  if (text.trim() === '') {
    throw new Error('getAutoColorBadgeColors: text cannot be empty')
  }

  // Generate deterministic hue from text using djb2 hash
  // Use golden angle (~137.508°) for better color distribution around the wheel
  const hash = hashValue(text)
  const goldenAngle = 137.508
  const hue = Math.round((hash * goldenAngle) % 360)

  // Vary saturation between 55-75% using a different part of the hash
  // This adds visual variety while maintaining vibrancy
  const saturation = 55 + (hash % 21)

  // Adjust lightness based on theme for WCAG accessibility
  // Light theme: darker background (35%) with white text
  // Dark theme: lighter background (80%) with dark text
  const lightness = theme === 'light' ? 35 : 80
  const textColor = theme === 'light' ? '#ffffff' : '#1a1a1a'

  return {
    background: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
    text: textColor,
  }
}
